/**
 * v4.10.0 — Lightweight First-Time Operator Guided Setup.
 *
 * One-shot dismissible card. Subtle slate/emerald surface, never amber/red.
 * Concise copy (≤3 short bullets) sourced from i18n. Non-modal, inline.
 * Per-user dismissal via localStorage, reopenable via <HelpGlyph/>.
 *
 * Strictly NOT:
 *   - a tutorial engine
 *   - a multi-step modal flow
 *   - a progress tracker
 *   - a learning system
 */
import { useTranslation } from "react-i18next";
import { Sparkles, X, PlayCircle } from "lucide-react";
import { useSetupGuide } from "./useSetupGuide";

export const SetupGuidanceCard = ({ guideKey, points = [], testId }) => {
    const { t } = useTranslation();
    const { dismissed, dismiss, explainerUrl } = useSetupGuide(guideKey);
    if (dismissed) return null;

    return (
        <div
            data-testid={testId || `setup-guide-${guideKey}`}
            className="relative rounded-xl p-4 sm:p-5 mb-4"
            style={{
                background: "linear-gradient(180deg, rgba(16,185,129,0.05), rgba(15,23,42,0.02))",
                border: "1px solid rgba(16,185,129,0.18)",
            }}
        >
            <div className="flex items-start gap-3">
                <div
                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: "rgba(16,185,129,0.12)", color: "#0F766E" }}
                    aria-hidden
                >
                    <Sparkles size={15} strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                    <p
                        className="text-[11px] uppercase tracking-wider font-medium mb-1"
                        style={{ color: "#0F766E" }}
                    >
                        {t("setup_guide.eyebrow")}
                    </p>
                    <h3
                        className="text-sm sm:text-base font-semibold text-foreground"
                        style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                        {t(`setup_guide.${guideKey}.title`)}
                    </h3>
                    {points.length > 0 && (
                        <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                            {points.slice(0, 3).map((p) => (
                                <li key={p} className="flex items-start gap-2">
                                    <span
                                        className="mt-1.5 inline-block w-1 h-1 rounded-full flex-shrink-0"
                                        style={{ background: "#0F766E" }}
                                    />
                                    <span>{t(p)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {explainerUrl && (
                        <a
                            href={explainerUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`${testId || `setup-guide-${guideKey}`}-explainer`}
                            className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium hover:underline"
                            style={{ color: "#0F766E" }}
                        >
                            <PlayCircle size={13} strokeWidth={1.75} />
                            {t("setup_guide.watch_intro")}
                        </a>
                    )}
                </div>
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label={t("setup_guide.dismiss")}
                    data-testid={`${testId || `setup-guide-${guideKey}`}-dismiss`}
                    className="flex-shrink-0 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                >
                    <X size={14} strokeWidth={1.75} />
                </button>
            </div>
        </div>
    );
};

export default SetupGuidanceCard;
